import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { StripeServerForm } from '~/components/billing/StripeServerForm';
import type { AnnualOffer } from '~/utils/annualOffer';

import styles from './Account.redesign.module.css';

/**
 * Encart « Passez à l'annuel : 2 mois offerts » (page Mon compte). Le bouton ouvre le portail
 * Stripe directement sur l'écran de confirmation de l'annuel (`intent=annual`, stripe-b2b
 * annualSwitch.js) ; retour sur `/account/annual?client=<id>` (« déjà en formule annuelle »).
 * `fromEmail` : arrivée par le lien de l'email automatique (`/account/annual?client=<id>`) →
 * encart amené à l'écran et brièvement mis en lumière. Mesure : `account_annual_offer_view` à
 * l'affichage, `account_annual_offer_click` au clic, avec `source` = `email` | `account` | `portal`
 * (`portal` : retour du portail sans changement de formule — paiement à valider, abandon).
 */
export function AnnualOfferCard({
    clientID,
    offer,
    status,
    fromEmail,
    fromPortal = false,
}: {
    clientID: string;
    offer: AnnualOffer;
    status: string;
    fromEmail: boolean;
    fromPortal?: boolean;
}) {
    const { t, i18n } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    let source = fromEmail ? 'email' : 'account';
    if (fromPortal) source = 'portal';

    useEffect(() => {
        logEvent('account_annual_offer_view', 'Account', { clientID, source, status });
        if (fromEmail) ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        // Une mesure par affichage de l'encart (pas à chaque relecture du statut).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div ref={ref} className={`${styles.annualOffer} ${fromEmail ? styles.annualFocus : ''}`}>
            <p className={styles.annualTitle}>{t('account.annual.title')}</p>
            <p>
                {t(offer.trialing ? 'account.annual.trialBody' : 'account.annual.body', {
                    annual: offer.annual,
                    twelveMonths: offer.twelveMonths,
                })}
            </p>
            <p className={styles.annualSteps}>{t('account.annual.steps')}</p>
            <StripeServerForm
                className={styles.inlineForm}
                route="/create-customer-portal-session"
                clientId={clientID}
                lang={i18n.language}
                intent="annual"
            >
                <button
                    type="submit"
                    className={styles.saveBtn}
                    onClick={() => logEvent('account_annual_offer_click', 'Account', { clientID, source, status })}
                >
                    {t('account.annual.cta')}
                </button>
            </StripeServerForm>
        </div>
    );
}

/**
 * Lien de l'email d'offre annuelle alors que l'encart ne s'affiche pas (déjà à l'année, membre non
 * admin, plus proposable, statut illisible) : une phrase plutôt qu'une page sans l'offre annoncée.
 * Mesure : `account_annual_link_unavailable` (`reason`), une fois par affichage. Retour du portail
 * après un passage réussi (`fromPortal` + `already_yearly`) : `account_annual_switched`.
 */
export function AnnualLinkNotice({
    clientID,
    reason,
    messageKey,
    fromPortal = false,
}: {
    clientID: string;
    reason: string;
    messageKey: string;
    fromPortal?: boolean;
}) {
    const { t } = useTranslation();
    useEffect(() => {
        if (fromPortal && reason === 'already_yearly') {
            logEvent('account_annual_switched', 'Account', { clientID, source: 'portal' });
            return;
        }
        logEvent('account_annual_link_unavailable', 'Account', {
            clientID,
            source: fromPortal ? 'portal' : 'email',
            reason,
        });
    }, [clientID, reason, fromPortal]);
    return (
        <p
            className={`${styles.msg} ${reason === 'already_yearly' ? styles.msgSuccess : ''} ${styles.keepNotice}`}
            role="status"
        >
            {t(messageKey)}
        </p>
    );
}
